/**
 * Partner Application Form
 * Client component for submitting sponsorship applications
 */

'use client';

import { useState } from 'react';
import { SponsorshipSchema } from '@/lib/validators';
import type { z } from 'zod';
import { functions, httpsCallable } from '@/lib/firebase.client';

type FormData = z.infer<typeof SponsorshipSchema>;

export default function PartnerForm() {
  const [formData, setFormData] = useState<Partial<FormData>>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companyWebsite: '',
    productName: '',
    productUrl: '',
    description: '',
    sponsorshipDuration: '3months',
    budget: undefined,
    goals: '',
    targetAudience: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form data
      const validated = SponsorshipSchema.parse(formData);

      // Submit to Cloud Function
      const createSponsorshipCallable = httpsCallable(functions, 'createSponsorship');
      const response = await createSponsorshipCallable(validated);
      const data = response.data as { success: boolean; sponsorshipId?: string; error?: string };

      if (data.success) {
        setSuccess(true);
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          companyWebsite: '',
          productName: '',
          productUrl: '',
          description: '',
          sponsorshipDuration: '3months',
          budget: undefined,
          goals: '',
          targetAudience: '',
        });
      } else {
        setError(data.error || 'Failed to submit application');
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit application. Please check your information and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl shadow-lg border-2 border-green-200 p-12 text-center">
        <div className="text-6xl mb-6">✅</div>
        <h3 className="text-3xl font-bold text-gray-900 mb-4">Application Received!</h3>
        <p className="text-xl text-gray-700 mb-2">
          Thank you for your interest in partnering with Can I Use My FSA.
        </p>
        <p className="text-gray-600">
          Our team will review your application and contact you within 24-48 hours.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="mt-8 bg-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
        >
          Submit Another Application
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 p-8">
      {error && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Company Information */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span>🏢</span> Company Information
        </h3>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="Your Company Inc."
              />
            </div>
            <div>
              <label htmlFor="contactName" className="block text-sm font-semibold text-gray-700 mb-2">
                Contact Name *
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="John Doe"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="john@company.com"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone (Optional)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label htmlFor="companyWebsite" className="block text-sm font-semibold text-gray-700 mb-2">
              Company Website *
            </label>
            <input
              type="url"
              id="companyWebsite"
              name="companyWebsite"
              value={formData.companyWebsite}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
              placeholder="https://www.yourcompany.com"
            />
          </div>
        </div>
      </div>

      {/* Product Information */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span>📦</span> Product Information
        </h3>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="productName" className="block text-sm font-semibold text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="Product or Service Name"
              />
            </div>
            <div>
              <label htmlFor="productUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                Product URL (Optional)
              </label>
              <input
                type="url"
                id="productUrl"
                name="productUrl"
                value={formData.productUrl}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
                placeholder="https://www.yourcompany.com/product"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
              Product Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
              placeholder="Describe your FSA/HSA eligible product or service..."
            />
          </div>
        </div>
      </div>

      {/* Sponsorship Details */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span>🎯</span> Sponsorship Details
        </h3>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sponsorshipDuration" className="block text-sm font-semibold text-gray-700 mb-2">
                Sponsorship Duration *
              </label>
              <select
                id="sponsorshipDuration"
                name="sponsorshipDuration"
                value={formData.sponsorshipDuration}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none bg-white"
              >
                <option value="1month">1 Month</option>
                <option value="3months">3 Months (10% off)</option>
                <option value="6months">6 Months (15% off)</option>
                <option value="12months">12 Months (25% off)</option>
              </select>
            </div>
            <div>
              <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                Budget Range (Optional)
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none bg-white"
              >
                <option value="">Select budget range</option>
                <option value="under5k">Under $5,000</option>
                <option value="5k-10k">$5,000 - $10,000</option>
                <option value="10k-25k">$10,000 - $25,000</option>
                <option value="25k+">$25,000+</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="goals" className="block text-sm font-semibold text-gray-700 mb-2">
              Marketing Goals *
            </label>
            <textarea
              id="goals"
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
              placeholder="What are your main goals for this sponsorship? (e.g., brand awareness, direct sales, lead generation)"
            />
          </div>

          <div>
            <label htmlFor="targetAudience" className="block text-sm font-semibold text-gray-700 mb-2">
              Target Audience (Optional)
            </label>
            <input
              type="text"
              id="targetAudience"
              name="targetAudience"
              value={formData.targetAudience}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none"
              placeholder="e.g., Parents, Seniors, Healthcare professionals"
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-4 rounded-xl font-bold text-lg hover:from-primary-600 hover:to-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Submitting...' : '🚀 Submit Application'}
      </button>

      <p className="text-center text-gray-600 text-sm mt-4">
        * Required fields. We'll respond within 24-48 hours.
      </p>
    </form>
  );
}

